import i18next from 'i18next';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { useTranslation } from 'react-i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { NotificationModel as Model, NotificationRequest } from '../model/notification.model';
import { NotificationService as Service } from '../services/notification.service';
import { useContext, useEffect, useState } from 'react';
import { formatLocalizedDateTime, formatTimeSince } from 'common/helpers/dateUtils';
import { tableHelper } from 'common/helpers/table/table-helper';
import { AlertTypeIcon } from 'common/helpers/alerts/alert-type-icon';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import NotificationContext from '../context/notification.context';
import { MainLayout, MasterLayout, ToolbarLayout, ToolbarLayoutLeft, ToolbarLayoutRight } from 'common/imports/content-layout';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { NavigateBefore } from '@mui/icons-material';

interface NotificationListProps {
    view: (notification: Model) => void;
}

export function NotificationList(props: NotificationListProps) {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const navigate = useNavigate();

    const { t } = useTranslation();
    const { filter } = useContext(NotificationContext);

    const [models, setModels] = useState<Model[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [request, setRequest] = useState<NotificationRequest>({
        isRead: undefined,
        order: "desc",
        pageIndex: 0,
        pageSize: 10,
        sort: "id",
    });

    const fetchData = async function (request: NotificationRequest) {
        try {
            const response = await Service.getDataList(request);
            if (response) {
                if (!response.hasErrors) {
                    setModels(response.data);
                    setTotalRows(response.totalItems);
                } else {
                    toast.error(`${t("notifications.errors.noResponse")}`);
                    console.error(response.errorCode);
                }
            } else {
                toast.error(`${t("notifications.errors.noResponse")}`);
            }
        } catch (error) {
            toast.error(`${t("notifications.errors.noResponse")}`);
            console.error(error)
        }
    }

    const columns: TableColumn<Model>[] = [
        {
            name: `${t('notifications.table.columns.title')}`,
            width: "300px",
            cell: (row: Model) => (
                <>
                    <div className="symbol symbol-35px me-3 ms-1">
                        <AlertTypeIcon notificationType={row.type} />
                    </div>
                    <span>{row.title}</span>
                </>
            )
        },
        {
            name: `${t('notifications.table.columns.message')}`,
            width: '500px',
            cell: (row) => (

                <div
                    className="d-flex align-items-center"
                    style={{
                        width: '100%',
                        maxWidth: '500px',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                    }}
                >
                    <span className="fw-semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.content}
                    </span>
                </div>
            )
        },
        {
            name: `${t('notifications.table.columns.author')}`,
            width: '200px',
            cell: (row) => (
                <div className="d-flex align-items-center" style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    <span className="fw-semibold">{row.author}</span>
                </div>
            )
        },
        {
            name: `${t('notifications.table.columns.since')}`,
            width: "auto",
            right: true,
            selector: (row) => formatLocalizedDateTime(row.date.toLocaleString(), 'llll') || '',
        }
    ]

    const handleRowsPerPage = (pageSize: number) => {
        if (pageSize <= 0)
            return;

        setModels([]);
        setRequest(getRequestUpdated({ pageSize: pageSize }));
    };

    const handlePageChange = (newPage: number) => {
        if (newPage <= 0)
            return;

        setModels([]);
        setRequest(getRequestUpdated({ pageIndex: newPage - 1 }));
    };

    async function FilterByName(filter: string) {
        request.search = filter;
        setRequest({
            order: request.order,
            pageIndex: request.pageIndex,
            pageSize: request.pageSize,
            sort: request.sort,
            notificationType: request.notificationType,
            isRead: request.isRead,
            search: request.search
        });
    }

    const getRequestUpdated = (parameters: NotificationRequest) => {
        return {
            order: parameters.order ?? request.order,
            pageIndex: parameters.pageIndex ?? request.pageIndex,
            pageSize: parameters.pageSize ?? request.pageSize,
            sort: parameters.sort ?? request.sort,
            notificationType: parameters.notificationType ?? request.notificationType,
            isRead: parameters.isRead ?? request.isRead,
            search: request.search
        }
    }

    useEffect(() => {
        fetchData(getRequestUpdated({
            isRead: filter.read,
            notificationType: filter.type
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request, filter]);

    return (
        <div className="card card-flush">
            <div className="card-body">
                <DataTable
                    title={tableHelper.TableHeader((e: any) => FilterByName(e.target.value))}
                    persistTableHead
                    columns={columns}
                    data={models}
                    noDataComponent={tableHelper.NoDataComponent()}
                    selectableRowsHighlight
                    dense
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationPerPage={request.pageSize ?? 10}
                    paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPage}
                    sortServer
                    onRowClicked={(r) => navigate(`/notifications/center/${r.id}`)}
                    progressPending={false}
                    progressComponent={tableHelper.Loader()}
                    contextMessage={tableHelper.ContextMessage()}
                    highlightOnHover
                    customStyles={itLabTableStyle}
                />
            </div>
        </div>
    );
}